﻿import * as angular from "angular";

import "../Extensions/ArrayExtensions";
import "../Extensions/StringExtensions";
import "../Extensions/DateExtensions";

import { MaintenanceMessage } from "../Model/MaintenanceMessage";
import { User } from "../Model/User";
import DataService from "../Services/DataService";

interface LoginModel {
    maintenance: boolean;
    reauthenicate: boolean;
    wasReauthenicate: boolean;
    initUsername: string;
    message?: string;
    mask?: string;
    remember: boolean;
    trust: boolean;
    username: string;
    password: string;
    loginLabel: string;
    title: string;
    stage: number;
    loading: boolean;
    isSubmit: boolean;
    lastResend?: Date;
    timeoutRemaining: number;
}

interface LoginActions {
    validate: ($event: Event) => Promise<void>;
    checkLogout: ($event: Event) => void;
    hasCapsLock: () => boolean;
    second: ($event: Event) => void;
    secondResend: ($event: Event) => void;
}

interface LoginScope extends angular.IScope {
    model: LoginModel;
    actions: LoginActions;
}

class LoginController {
    private $scope: LoginScope;
    private $sce: angular.ISCEService;
    private $rootScope: angular.IRootScopeService;
    private dataService: DataService;

    private _frame: HTMLElement | null;
    private _loaded: boolean;
    private _caps: boolean;

    private _chkTrust: HTMLInputElement | null;
    private _inResend: HTMLInputElement | null;

    constructor(
        $scope: LoginScope,
        $sce: angular.ISCEService,
        $rootScope: angular.IRootScopeService,
        dataService: DataService,
    ) {
        this.$scope = $scope;
        this.$sce = $sce;
        this.$rootScope = $rootScope;
        this.dataService = dataService;

        this._frame = document.querySelector<HTMLElement>(".login-frame");
        this._loaded = false;
        this._caps = false;

        this._chkTrust = document.querySelector<HTMLInputElement>(
            "#chkTrust",
        ) as HTMLInputElement | null;
        this._inResend = document.querySelector<HTMLInputElement>(
            "#resend",
        ) as HTMLInputElement | null;

        $scope.model = {
            maintenance: true,
            reauthenicate: false,
            wasReauthenicate: false,
            initUsername: "",
            remember: localStorage.getItem("remember") === "true" || false,
            trust: this._chkTrust?.value === "true" || false,
            username: localStorage.getItem("username") || "",
            password: "",
            loginLabel: "Find Me",
            title: "Sign In",
            stage: 0,
            loading: false,
            isSubmit: false,
            lastResend: localStorage.getItem("lastResend")?.toDate(),
            timeoutRemaining: 0,
        };

        $scope.actions = {
            validate: this.validate.bind(this),
            checkLogout: this.checkLogout.bind(this),
            hasCapsLock: this.hasCapsLock.bind(this),
            second: this.second.bind(this),
            secondResend: this.secondResend.bind(this),
        };

        $scope.$watch("model.remember", newValue => {
            if (this._loaded) {
                localStorage.setItem("remember", newValue ? "true" : "false");
                if (!newValue) localStorage.removeItem("username");
            }
        });

        if (this.$scope.model.lastResend) this.TickTimeout();

        if (
            !Reflect.has(globalThis, "wasRedirect") ||
            !Reflect.get(globalThis, "wasRedirect")
        )
            this.initialise();
    }

    private TickTimeout() {
        if (this.$scope.model.lastResend) {
            const elapsed = new Date().secondsBetween(
                this.$scope.model.lastResend,
            );
            this.$scope.model.timeoutRemaining = Math.floor(
                Math.max(180 - elapsed, 0),
            );

            if (this.$scope.model.timeoutRemaining > 0)
                setTimeout(this.TickTimeout.bind(this), 1000);
        } else {
            this.$scope.model.timeoutRemaining = 0;
        }

        if (this.$scope.model.timeoutRemaining === 0) {
            this.$scope.model.lastResend = undefined;
            localStorage.removeItem("lastResend");
        }

        if (!this.$rootScope.$$phase) this.$scope.$apply();
    }

    private async initialise() {
        window.addEventListener("keyup", event => {
            if (event.which > 64 && event.which < 123) {
                this._caps =
                    event.getModifierState("CapsLock") ||
                    event.getModifierState("Shift");
            } else if (event.which === 20) {
                this._caps = !this._caps;
            }
            if (!this.$rootScope.$$phase) this.$scope.$apply();
        });

        const [maintainence, authenticated] = await Promise.all([
            this.dataService.maintenance(),
            this.dataService.authenticated(),
        ]);

        this.maintenance(maintainence);
        this.authenticated(authenticated);

        const secondFactor = Reflect.get(globalThis, "secondFactor");

        if (this.$scope.model.username.length > 0 && !secondFactor) {
            await this.validate();
            this._loaded = true;
        } else {
            if (secondFactor) this.promptSSO();
            this._loaded = true;
        }
    }

    private async validate($event?: Event) {
        if (!!$event) $event.preventDefault();

        if (this.$scope.model.loginLabel === "Use Passkey") {
            return await this.checkAuthMode();
        } else if (
            (this.$scope.model.stage === 0 &&
                this.$scope.model.username.length > 0) ||
            (this.$scope.model.stage === 1 &&
                this.$scope.model.password.length > 0) ||
            this.$scope.model.stage === 3
        ) {
            switch (this.$scope.model.stage) {
                case 0:
                    return await this.checkAuthMode();
                case 1:
                    this.$scope.model.stage++;
                    return await this.lockPassword();
                case 3:
                    this.login();
            }
        }
    }

    private checkLogout($event: Event) {
        $event.preventDefault();
        this.$scope.model.stage = 0;
        this.$scope.model.reauthenicate = false;
        this.$scope.model.loginLabel = "Find Me";
    }

    private hasCapsLock() {
        return this._caps;
    }

    private maintenance(data: MaintenanceMessage) {
        if (data) {
            let message = data.message
                    .replace(/\n/g, "<br/>")
                    .replace(/'/g, "'"),
                fromDate = data.downStartDate.toDate(),
                toDate = data.downEndDate.toDate();
            message = message.replace(/{start}/g, fromDate.toLocaleLong());
            message = message.replace(
                /{end}/g,
                fromDate[
                    fromDate.equalsDate(toDate)
                        ? "toLocaleShortTime"
                        : "toLocaleLong"
                ](),
            );
            this.$scope.model.message = this.$sce.trustAsHtml(message);
            this.$scope.model.maintenance = data.disableAccess;

            globalThis.addEventListener("keydown", event => {
                if (event.ctrlKey && event.altKey && event.key === "L") {
                    this.$scope.model.maintenance = false;
                }
            });
        } else {
            this.$scope.model.maintenance = false;
        }
        if (!this.$rootScope.$$phase) this.$scope.$apply();
    }

    private async authenticated(data: User) {
        if (data) {
            this.$scope.model.username = data.username;
            this.$scope.model.initUsername = data.username;
            this.$scope.model.loginLabel = "Re-authenticate";
            this.$scope.model.title = "Please re-authenticate";
            this.$scope.model.reauthenicate = true;
            this.$scope.model.wasReauthenicate = true;
            if (!this.$rootScope.$$phase) this.$scope.$apply();
        }
    }

    private async checkAuthMode() {
        this.$scope.model.loading = true;
        this.$scope.model.loginLabel = "";
        if (this.$scope.model.remember) {
            localStorage.setItem("username", this.$scope.model.username);
        }
        const result = await this.dataService.password(
            this.$scope.model.username,
        );

        switch (result) {
            case 1:
            case 2:
                if (this._loaded) this.login();
                else this.promptSSO();
                break;
            case 3:
                if (!(await this.promptPasswordless())) this.promptPassword();
                break;
            default:
                this.promptPassword();
                break;
        }

        this.$scope.model.loading = false;
        if (!this.$rootScope.$$phase) this.$scope.$apply();
    }

    private async promptPasswordless() {
        this.$scope.model.loading = true;
        let publicKeyOptions;
        try {
            publicKeyOptions = await this.dataService.passwordless(
                this.$scope.model.username,
            );
        } catch (e) {
            alert(e);
            this.$scope.model.loginLabel = "Authenticate";
            this.$scope.model.loading = false;
            if (!this.$rootScope.$$phase) this.$scope.$apply();
            return false;
        }

        if (typeof publicKeyOptions.challenge === "string")
            publicKeyOptions.challenge =
                publicKeyOptions.challenge.fromBase64();

        for (const item of publicKeyOptions.allowCredentials!) {
            item.id = (item.id as unknown as string).fromBase64();
            item.type = "public-key";
            delete item.transports;
        }

        publicKeyOptions.userVerification = "required";

        // ask browser for credentials (browser will ask connected authenticators)
        let credential;
        try {
            credential = await navigator.credentials.get({
                publicKey: publicKeyOptions,
            });

            try {
                await this.verifyAssertionWithServer(credential!);

                this.$scope.model.stage = 3;
            } catch (e) {
                alert("Could not verify assertion");
                this.$scope.model.loginLabel = "Authenticate";
                this.$scope.model.loading = false;
                if (!this.$rootScope.$$phase) this.$scope.$apply();
            }
            return true;
        } catch (err: any) {
            const message = err.message ? err.message : err;
            if (message === "The document is not focused.") {
                this.$scope.model.loginLabel = "Use Passkey";
                this.$scope.model.loading = false;
                this.$scope.model.stage = 3;
                if (!this.$rootScope.$$phase) this.$scope.$apply();
                return true;
            } else {
                console.error(err.message ? err.message : err);
                this.$scope.model.loginLabel = "Authenticate";
                this.$scope.model.loading = false;
                if (!this.$rootScope.$$phase) this.$scope.$apply();
                return false;
            }
        }
    }

    private async verifyAssertionWithServer(assertedCredential: Credential) {
        document.querySelector<HTMLInputElement>("#txtData")!.value =
            JSON.stringify(assertedCredential);

        this.login();
    }

    private promptSSO() {
        this.$scope.model.stage = 3;
        this.$scope.model.loginLabel = "Authenticate";
        if (!this.$rootScope.$$phase) this.$scope.$apply();
        setTimeout(() => {
            document.body
                .querySelector<HTMLElement>("#txtTwoFactorCode")
                ?.focus();
        }, 100);
    }

    private promptPassword() {
        this.$scope.model.stage = 1;
        this.$scope.model.loginLabel = "Sign In";
        if (!this.$rootScope.$$phase) this.$scope.$apply();
        setTimeout(() => {
            document.body.querySelector<HTMLElement>(".password")?.focus();
        }, 100);
    }

    private async lockPassword() {
        let temp = "";
        for (let index = 0; index < this.$scope.model.password.length; index++)
            temp += "&bull;";
        this.$scope.model.mask = this.$sce.trustAsHtml(temp);
        this.login();
    }

    private async login() {
        this._frame!.style.height = `${this._frame?.offsetHeight}px`;
        this._frame!.classList.add("loading");
        this._frame!.style.height = "200px";
        this.$scope.model.isSubmit = true;

        if (
            this.$scope.model.wasReauthenicate &&
            this.$scope.model.initUsername !== this.$scope.model.username
        )
            document.querySelector<HTMLInputElement>("#WasChangeUser")!.value =
                "true";

        if (!this.$rootScope.$$phase) this.$scope.$apply();

        setTimeout(() => {
            document.forms[0].submit();
        }, 700);
    }

    private second($event: Event) {
        if ($event instanceof KeyboardEvent && $event.key === "Enter") {
            $event.preventDefault();

            this._frame!.style.height = `${this._frame?.offsetHeight}px`;
            this._frame!.classList.add("loading");
            this._frame!.style.height = "200px";
            this.$scope.model.isSubmit = true;

            if (!!this._chkTrust)
                this._chkTrust.value = this.$scope.model.trust
                    ? "true"
                    : "false";

            if (!this.$rootScope.$$phase) this.$scope.$apply();

            setTimeout(() => {
                document.forms[0].submit();
            }, 700);
        }
    }

    private secondResend() {
        if (!!this._inResend && this.$scope.model.timeoutRemaining <= 0) {
            this._inResend.value = "resend";
            localStorage.setItem("lastResend", new Date().toISOString());
            document.forms[0].submit();
        }
    }
}

angular
    .module("datastore")
    .controller("LoginController", [
        "$scope",
        "$sce",
        "$rootScope",
        "dataService",
        LoginController,
    ]);

export default LoginController;
